import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import Switch from 'react-switch';
import styled, { useTheme } from 'styled-components';
import { BodyText, FlexRow, SvgIcon } from '@make-software/cspr-ui';
import { checkDefaultLengthRange, invalidNameMessage, validateNumbersOnly } from 'app/validators';
import {
  ButtonsContainer,
  centerModalStyles,
  ErrorText,
  ErrorTextContainer,
  ModalContainer,
  StyledCaption,
  StyledCaptionText,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput,
  StyledToggle,
  TOGGLE_ACTIVE_COLOR
} from 'app/components/home/components/create-modal-styles';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { ErrorResult } from 'app/build-api/types';
import { AccessItem, InviteUserResponseType, UpdateUserRequestType, UserRole } from 'app/build-api';
import { ExpectedErrors } from 'app/components/home/components/users/user-container';
import { UserAccessList } from 'app/components/home/components/users/components/user-access-list';

const EditUserModalContainer = styled(ModalContainer)(({ theme }) =>
  theme.withMedia({
    overflowY: 'scroll',
    maxHeight: '70svh'
  })
);

export interface EditUserWindowProps {
  isOpen: boolean;
  userToEdit: InviteUserResponseType;
  title: string;
  inputPlaceholder?: string;
  confirmLabel: string;
  onUpdate: (id: string, data: UpdateUserRequestType) => void;
  dismissLabel?: string;
  onDismiss: () => void;
  expectedError?: ExpectedErrors | undefined;
  unexpectedError?: ErrorResult | null;
  portalClass?: string;
}

export const EditUserWindow = ({
  isOpen,
  userToEdit,
  title,
  confirmLabel,
  onUpdate,
  dismissLabel,
  onDismiss,
  expectedError,
  unexpectedError,
  portalClass = 'portal'
}: EditUserWindowProps) => {
  const theme = useTheme();
  const modalStyle = {
    overlay: {
      backgroundColor: theme.styleguideColors.backgroundOverlay,
      zIndex: 15
    },
    content: {
      ...centerModalStyles,
      ...{
        border: 'none',
        backgroundColor: theme.styleguideColors.backgroundPrimary,
        borderTop: `4px solid ${theme.styleguideColors.contentRed}`,
        borderColor: theme.styleguideColors.contentRed,
        boxShadow: '0px 16px 48px rgba(26, 25, 25, 0.2)'
      }
    }
  };

  const { full_name, is_active, access_list, id, email: incomeEmail, role } = userToEdit;

  const [email, setEmail] = useState<string>(incomeEmail);
  const [name, setName] = useState<string>(full_name);
  const [isStatusActive, setIsStatusActive] = useState<boolean>(is_active);
  const [userAccessList, setUserAccess] = useState<AccessItem[]>(access_list);
  const [isAccessChanged, setIsAccessChanged] = useState<boolean>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<Record<'name', string>>({
    name: ''
  });

  const clearForm = () => {
    setName(full_name);
    setEmail(incomeEmail);
    setIsStatusActive(is_active);
    setUserAccess(access_list);
    setFormErrors({
      ...formErrors,
      name: ''
    });
  };

  useEffect(() => {
    setShowModal(isOpen);
    clearForm();
  }, [isOpen]);

  const handleName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setName(val);
  };

  const handleNameOnBlur = () => {
    setFormErrors({
      ...formErrors,
      name: validateNumbersOnly(name) ? invalidNameMessage : checkDefaultLengthRange(name)
    });
  };

  const handleChangeUserStatus = () => {
    setIsStatusActive((prevState) => !prevState);
  };

  const handleAccessChange = (access: AccessItem[]) => {
    setUserAccess(access);
    setIsAccessChanged(true);
  };

  const handleConfirm = () => {
    if (!email.length || !name.length) {
      setFormErrors({
        ...formErrors,
        name: !name.length
          ? 'This field is required'
          : validateNumbersOnly(name)
            ? invalidNameMessage
            : ''
      });
      return;
    }

    onUpdate &&
      !formErrors.name &&
      onUpdate(id, {
        full_name: name,
        access_list: userAccessList,
        is_active: isStatusActive
      });
  };

  const handleOnDismiss = () => {
    clearForm();
    onDismiss();
  };

  const showAppropriateError = () => {
    let specificError = '';
    expectedError &&
      Object.values(expectedError).some((item) => {
        if (Boolean(item)) {
          specificError = item;
        }
      });

    return (
      specificError && (
        <ErrorTextContainer justify={'center'}>
          <ErrorText size={3}>{specificError}</ErrorText>
        </ErrorTextContainer>
      )
    );
  };

  return (
    <>
      {showModal && (
        <ReactModal
          isOpen={showModal}
          style={modalStyle}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick={false}
          portalClassName={portalClass}
        >
          <EditUserModalContainer gap={20}>
            {unexpectedError ? (
              <ErrorWindow
                title={unexpectedError.code || ''}
                information={unexpectedError.message}
                withModal
                icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
              />
            ) : (
              <>
                <StyledCaption>
                  <StyledCaptionText size={1} scale="lg">
                    {title}
                  </StyledCaptionText>
                </StyledCaption>
                <FlexRow justify="center">
                  <StyledInput
                    value={name}
                    label={<BodyText size={1}>Full Name</BodyText>}
                    placeholder="User name"
                    onChange={handleName}
                    onBlur={handleNameOnBlur}
                    error={!!formErrors.name}
                    validationText={formErrors.name}
                  />
                </FlexRow>
                <FlexRow justify="center">
                  <StyledInput
                    value={email}
                    label={<BodyText size={1}>Email</BodyText>}
                    placeholder="User email"
                    disabled
                  />
                </FlexRow>

                {showAppropriateError()}

                <UserAccessList
                  currentAccessList={userAccessList}
                  currentUserRole={role}
                  onSelectRole={handleAccessChange}
                />
                <StyledToggle>
                  <BodyText size={1}>Active status:</BodyText>
                  <Switch
                    checked={isStatusActive}
                    onChange={handleChangeUserStatus}
                    onColor={TOGGLE_ACTIVE_COLOR}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    disabled={role === UserRole.Owner}
                  />
                </StyledToggle>
                <ButtonsContainer justify={'space-between'}>
                  {dismissLabel && (
                    <StyledDismissButton color={'utility'} onClick={handleOnDismiss}>
                      {dismissLabel}
                    </StyledDismissButton>
                  )}
                  <StyledConfirmButton onClick={handleConfirm}>{confirmLabel}</StyledConfirmButton>
                </ButtonsContainer>
              </>
            )}
          </EditUserModalContainer>
        </ReactModal>
      )}
    </>
  );
};

export default EditUserWindow;
